import { f0FormField, F0Field } from '@factorialco/f0-react'
import { z } from 'zod'

import { CustomFiltersWithNoExtraFilters, EmployeeSelectorV2Props } from './types'
import { EntitySelectorV2Messages } from 'modules/core/components/EntitySelectorV2/types'
import type { EmployeeSelectorFieldConfig } from 'modules/employees/lib/formFields/employee/types'

// Exclude placeholder from messages since it's provided by f0FormField's render props
type FieldConfigMessages = Omit<EntitySelectorV2Messages, 'placeholder'>

type FieldConfig = Partial<
  Pick<EmployeeSelectorV2Props, 'options' | 'forcedFilters' | 'hint' | 'useAccessId'>
> & {
  filters?: CustomFiltersWithNoExtraFilters
  messages?: FieldConfigMessages
}

type BaseEmployeeSelectorV2FormFieldParams = {
  label: string
  section: string
  helpText?: string
  fieldConfig?: FieldConfig
  renderIf?: F0Field['renderIf']
}

function toEmployeeSelectorFieldConfig(
  fieldConfig: FieldConfig | undefined,
  extra: { mandatory?: boolean; multiple?: boolean }
): EmployeeSelectorFieldConfig {
  return {
    type: 'employee-selector' as const,
    options: fieldConfig?.options,
    forcedFilters: fieldConfig?.forcedFilters,
    hint: fieldConfig?.hint,
    useAccessId: fieldConfig?.useAccessId,
    filters: fieldConfig?.filters,
    messages: fieldConfig?.messages,
    mandatory: extra.mandatory,
    multiple: extra.multiple,
  }
}

const createOptionalMultiSchema = () =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('manual'),
      ids: z.array(z.string()),
    }),
    z.object({
      type: z.literal('all'),
      filters: z.record(z.string(), z.unknown()).optional(),
      excludedIds: z.array(z.string()),
      includeInactive: z.boolean().optional(),
    }),
  ])

const optionalMultiEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createOptionalMultiSchema()

  return f0FormField<typeof schema, EmployeeSelectorFieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    customFieldName: 'employee-selector',
    fieldConfig: toEmployeeSelectorFieldConfig(fieldConfig, { mandatory: false, multiple: true }),
    renderIf,
  })
}

const createMandatoryMultiSchema = () =>
  z.discriminatedUnion('type', [
    z.object({
      type: z.literal('manual'),
      ids: z.array(z.string()).min(1),
    }),
    z.object({
      type: z.literal('all'),
      filters: z.record(z.string(), z.unknown()).optional(),
      excludedIds: z.array(z.string()),
      includeInactive: z.boolean().optional(),
    }),
  ])

const mandatoryMultiEmployeeSelectorV2FormField = ({
  label,
  section,
  helpText,
  fieldConfig,
  renderIf,
}: BaseEmployeeSelectorV2FormFieldParams) => {
  const schema = createMandatoryMultiSchema()

  return f0FormField<typeof schema, EmployeeSelectorFieldConfig>(schema, {
    label,
    section,
    helpText,
    fieldType: 'custom',
    customFieldName: 'employee-selector',
    fieldConfig: toEmployeeSelectorFieldConfig(fieldConfig, { mandatory: true, multiple: true }),
    renderIf,
  })
}

type MultiEmployeeSelectorV2FormFieldParams<M extends boolean> =
  BaseEmployeeSelectorV2FormFieldParams & { mandatory: M }

export const multiEmployeeSelectorV2FormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiEmployeeSelectorV2FormFieldParams<M>) => {
  return mandatory
    ? mandatoryMultiEmployeeSelectorV2FormField({ ...rest, renderIf })
    : optionalMultiEmployeeSelectorV2FormField({ ...rest, renderIf })
}
